import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 반짝이는 테두리 하나 — 「다음 할 일」 띠가 가리키는 곳을 짚어 준다 (DOC-31 §4.1).
 *
 * <h2>안내 투어의 반짝임을 그대로 쓴다</h2>
 * 모양은 {@code .tour-sparkle} 이다({@link TourOverlay} 의 보조 하이라이트와 <b>같은 CSS</b>).
 * 처음에는 대상에 인라인 {@code outline} 을 직접 발랐는데, 요소의 원래 테두리와 겹쳐 각이 어긋나고
 * 색도 화면마다 달라졌다. 이미 있는 반짝임을 쓰면 <b>? 안내에서 본 것과 같은 표시</b>가 되어
 * "저기를 보라는 뜻" 이라는 것을 따로 배우지 않아도 된다.
 *
 * <h2>투어와 달리 막을 깔지 않는다</h2>
 * 투어는 설명 도중 다른 곳을 누르지 못하게 화면을 덮는다. 쉬운 모드는 반대다 — <b>지금 그 버튼을
 * 누르라고</b> 하는 것이라 막으면 안 된다. 그래서 어두운 막도, 클릭 차단도 없다.
 *
 * <h2>대상이 없으면 아무것도 그리지 않는다</h2>
 * 권한이 없어 버튼이 없는 사람에게 허공을 반짝이지 않는다. 조건은 안내 투어와 같다 —
 * DOM 에 있고, 실제로 보이고, 화면 안에 있어야 한다.
 *
 * <h2>AI 도 같은 반짝임을 쓴다 (2026-09-23)</h2>
 * 「자료 입력 어디서 해?」 에 AI 가 <b>말 대신 짚는다</b>({@code highlight} 이벤트). 그때 대상은
 * 쉬운 모드의 {@code data-easy} 20곳이 아니라 안내 투어의 {@code data-tour} 173곳이다 —
 * 이름이 이미 붙어 있는 자리가 그쪽이기 때문이다. 그려지는 모양은 <b>같아야 한다</b>: 쉬운 모드에서
 * 본 반짝임과 AI 가 짚은 반짝임이 다르면, 사용자는 그것을 두 가지 신호로 배워야 한다.
 */

type Rect = { top: number; left: number; width: number; height: number };

/** 대상에 딱 붙지 않도록 두는 여백. 투어의 보조 하이라이트와 같은 값이다. */
const PADDING = 4;

/** 스크롤이 멎을 때까지 프레임마다 다시 재는 횟수. 부드러운 스크롤은 이벤트가 띄엄띄엄 온다. */
const TRACK_FRAMES = 45;

export function SpotlightRing({
  target,
  /**
   * 대상을 가리키는 속성. 쉬운 모드의 「다음 할 일」 띠는 {@code data-easy}(기본), AI 가 짚는
   * 자리는 {@code data-tour} 다. 두 이름 공간을 섞지 않는다 — 같은 낱말이 양쪽에 있으면
   * 띠가 AI 가 짚은 곳을 가리키게 된다.
   */
  attribute = 'data-easy',
  /**
   * 화면 밖이면 <b>굴려서 보여 준다</b>. AI 가 짚을 때만 참이다 — 사람이 「어디야」 하고 물었을
   * 때 그 자리가 스크롤 아래에 있으면, 반짝여 봐야 보이지 않는다.
   *
   * <p>쉬운 모드의 띠는 거짓이다. 그쪽은 사람이 입력하는 동안 대상이 계속 옮겨 다니므로
   * ({@code useEasyTargetRect} 머리말), 굴리면 글자를 치는 중에 화면이 제멋대로 움직인다.
   */
  scrollIntoView = false,
}: {
  target: string | null;
  attribute?: 'data-easy' | 'data-tour';
  scrollIntoView?: boolean;
}) {
  const rect = useEasyTargetRect(target, attribute, scrollIntoView);
  if (!rect) return null;

  return createPortal(
    <div
      className="tour-sparkle"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: rect.top - PADDING,
        left: rect.left - PADDING,
        width: rect.width + PADDING * 2,
        height: rect.height + PADDING * 2,
        // 누르라고 짚어 주는 표시다. 클릭을 가로채면 짚어 준 것을 누를 수 없다.
        pointerEvents: 'none',
        // 모달(1000번대) 안에서도 보여야 한다. 투어 오버레이(1001)보다는 아래에 둔다 —
        // ? 안내를 여는 동안에는 그쪽이 화면의 주인이다.
        zIndex: 999,
        transition: 'top 0.15s ease, left 0.15s ease, width 0.15s ease, height 0.15s ease',
      }}
    />,
    document.body,
  );
}

/**
 * `data-easy` 이름이 가리키는 요소의 화면상 위치.
 *
 * <p><b>매번 다시 찾는다.</b> 띠가 가리키는 대상은 사람이 입력하는 동안 나타나고 사라진다
 * (실적을 한 줄 넣으면 [저장] 이 다음 대상이 된다). 한 번 찾아 두면 그때 없던 버튼은 영영 못 찾는다.
 */
function useEasyTargetRect(name: string | null, attribute: string, scroll: boolean): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!name) {
      setRect(null);
      return;
    }

    /*
      화면 밖이면 굴려서 보여 준다(AI 가 짚을 때만 · §scrollIntoView). `center` 인 이유:
      `nearest` 는 자리가 머리글 아래에 반쯤 걸친 채 멈춰, 반짝임이 잘린 것으로 보인다.
      아래 `update` 가 프레임마다 다시 재므로 부드러운 스크롤을 테두리가 따라간다.
    */
    if (scroll) {
      document
        .querySelector<HTMLElement>(`[${attribute}="${name}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const update = () =>
      setRect((previous) => {
        const element = document.querySelector<HTMLElement>(`[${attribute}="${name}"]`);
        if (!isVisible(element)) return previous === null ? previous : null;

        const box = element!.getBoundingClientRect();
        const next = { top: box.top, left: box.left, width: box.width, height: box.height };
        // 값이 그대로면 상태를 갈지 않는다 — 매 프레임 새 객체를 만들면 아무것도 움직이지 않는데
        // 초당 60번 다시 그린다(투어 오버레이와 같은 방어).
        return same(previous, next) ? previous : next;
      });

    update();

    // scroll 은 capture 로 듣는다 — 스크롤하는 것이 window 가 아니라 모달 본문일 수 있다.
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    let frames = TRACK_FRAMES;
    let raf = requestAnimationFrame(function tick() {
      update();
      if (--frames > 0) raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [name, attribute, scroll]);

  return rect;
}

/** 화면에 실제로 보이는가. `display:none` 과 화면 밖으로 밀어 둔 것을 함께 거른다. */
function isVisible(element: HTMLElement | null): boolean {
  if (!element || element.offsetParent === null) return false;

  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0 && box.right > 0 && box.left < window.innerWidth;
}

function same(a: Rect | null, b: Rect): boolean {
  return (
    a !== null &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}
